import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, RefreshCw, FileText, Shield, Activity, 
  CheckCircle, AlertTriangle, XCircle, Clock, User,
  Download, Filter, ChevronLeft, ChevronRight, Eye,
  Server, Database, Zap, Mail, MessageSquare, Brain,
  DollarSign, Pill, TrendingUp, AlertCircle, Globe,
  BarChart3, Lock, Unlock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuditTrailExplorer() {
  const [activeTab, setActiveTab] = useState('audit');
  const [loading, setLoading] = useState(true);
  
  // Audit state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditStats, setAuditStats] = useState({});
  const [auditPagination, setAuditPagination] = useState({});
  const [filters, setFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDetailOpen, setLogDetailOpen] = useState(false);
  
  // Integration state
  const [integrations, setIntegrations] = useState([]);
  const [integrationSummary, setIntegrationSummary] = useState({});
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [integrationDetailOpen, setIntegrationDetailOpen] = useState(false);
  
  // API metrics state
  const [apiMetrics, setApiMetrics] = useState([]);
  const [apiSummary, setApiSummary] = useState({});
  
  // Dashboard state
  const [dashboard, setDashboard] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [phiOnly, setPhiOnly] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLogs();
    }
  }, [page, actionFilter, resourceFilter, phiOnly, searchTerm]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, filtersRes, integrationsRes, metricsRes] = await Promise.all([
        fetch(`${API_URL}/api/phase54/dashboard`),
        fetch(`${API_URL}/api/phase54/audit/filters`),
        fetch(`${API_URL}/api/phase54/integrations`),
        fetch(`${API_URL}/api/phase54/api-metrics`)
      ]);

      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setDashboard(data);
      }

      if (filtersRes.ok) {
        const data = await filtersRes.json();
        setFilterOptions(data);
      }

      if (integrationsRes.ok) {
        const data = await integrationsRes.json();
        setIntegrations(data.integrations || []);
        setIntegrationSummary(data.summary || {});
      }

      if (metricsRes.ok) {
        const data = await metricsRes.json();
        setApiMetrics(data.metrics || []);
        setApiSummary(data.summary || {});
      }

      await fetchAuditLogs();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '25'
      });
      
      if (actionFilter) params.append('action', actionFilter);
      if (resourceFilter) params.append('resource_type', resourceFilter);
      if (phiOnly) params.append('phi_only', 'true');
      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(`${API_URL}/api/phase54/audit/logs?${params}`);
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
        setAuditPagination(data.pagination || {});
        setAuditStats(data.statistics || {});
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    }
  };

  const handleViewLogDetail = async (logId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase54/audit/logs/${logId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedLog(data);
        setLogDetailOpen(true);
      }
    } catch (error) {
      toast.error('Failed to load log details');
    }
  };

  const handleTestIntegration = async (integrationId) => {
    toast.info('Testing integration...');
    try {
      const response = await fetch(`${API_URL}/api/phase54/integrations/${integrationId}/test`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.test_result === 'success') {
          toast.success(`${data.integration_name}: Connection successful (${data.response_time_ms}ms)`);
        } else {
          toast.error(`${data.integration_name}: Connection failed`);
        }
      }
    } catch (error) {
      toast.error('Failed to test integration');
    }
  };

  const handleViewIntegrationDetail = async (integrationId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase54/integrations/${integrationId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedIntegration(data);
        setIntegrationDetailOpen(true);
      }
    } catch (error) {
      toast.error('Failed to load integration details');
    }
  };

  const handleExportLogs = async () => {
    toast.success('Export initiated. Download link will be available shortly.');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'degraded':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'down':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      healthy: 'bg-green-100 text-green-700',
      degraded: 'bg-yellow-100 text-yellow-700',
      down: 'bg-red-100 text-red-700'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const getIntegrationIcon = (type) => {
    const icons = {
      emr: Database,
      pharmacy: Pill,
      dme: Server,
      billing: DollarSign,
      notification: Mail,
      ai: Brain,
      database: Database,
      cache: Zap
    };
    const Icon = icons[type] || Globe;
    return <Icon className="w-5 h-5" />;
  };

  const getActionColor = (action) => {
    const colors = {
      login: 'bg-blue-100 text-blue-700',
      logout: 'bg-gray-100 text-gray-700',
      view: 'bg-teal-100 text-teal-700',
      create: 'bg-green-100 text-green-700',
      update: 'bg-yellow-100 text-yellow-700',
      delete: 'bg-red-100 text-red-700',
      export: 'bg-purple-100 text-purple-700',
      print: 'bg-indigo-100 text-indigo-700',
      access: 'bg-orange-100 text-orange-700',
      sign: 'bg-emerald-100 text-emerald-700'
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="monitoring-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-teal-600 mb-4" />
          <p className="text-slate-600">Loading monitoring data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="monitoring-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="monitoring-title">
              <Activity className="w-7 h-7 text-teal-600" />
              System Monitoring
            </h1>
            <p className="text-slate-600 mt-1">Audit trail, integrations, and API performance</p>
          </div>
          <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Audit Events (24h)</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="stat-audit-events">{dashboard.audit?.total_events_24h || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-teal-600" />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">{dashboard.audit?.phi_access_count || 0} PHI access events</p>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Integrations</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="stat-integrations">{integrationSummary.healthy || 0}/{integrationSummary.total || 0}</p>
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${integrationSummary.down > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                  <Server className={`w-5 h-5 ${integrationSummary.down > 0 ? 'text-red-600' : 'text-green-600'}`} />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {integrationSummary.down > 0 ? `${integrationSummary.down} down` : 'All healthy'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">API Requests (24h)</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="stat-api-requests">{apiSummary.total_requests_24h?.toLocaleString() || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">{apiSummary.overall_error_rate || 0}% error rate</p>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avg Response</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="stat-avg-response">{apiSummary.avg_response_time_ms || 0}ms</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">{apiSummary.endpoint_count || 0} endpoints monitored</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alerts */}
      {dashboard?.alerts?.length > 0 && (
        <div className="mb-6">
          {dashboard.alerts.map((alert, idx) => (
            <div key={idx} className={`p-3 rounded-lg flex items-center gap-3 mb-2 ${alert.type === 'warning' ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <AlertCircle className={`w-5 h-5 ${alert.type === 'warning' ? 'text-red-500' : 'text-yellow-500'}`} />
              <span className="text-sm font-medium">{alert.message}</span>
              <span className="text-xs text-slate-400 ml-auto">{alert.time}</span>
            </div>
          ))}
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <FileText className="w-4 h-4 mr-2" />
            Audit Trail
          </TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">
            <Server className="w-4 h-4 mr-2" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="api" data-testid="tab-api">
            <BarChart3 className="w-4 h-4 mr-2" />
            API Performance
          </TabsTrigger>
        </TabsList>

        {/* Audit Trail Tab */}
        <TabsContent value="audit" className="space-y-4">
          {/* Filters */}
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search by user, resource, or action..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                    className="pl-9"
                    data-testid="audit-search"
                  />
                </div>
                <Select value={actionFilter || "all"} onValueChange={(v) => { setActionFilter(v === "all" ? "" : v); setPage(1); }}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {filterOptions.actions?.map((action) => (
                      <SelectItem key={action} value={action}>{action}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={resourceFilter || "all"} onValueChange={(v) => { setResourceFilter(v === "all" ? "" : v); setPage(1); }}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Resources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Resources</SelectItem>
                    {filterOptions.resource_types?.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant={phiOnly ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setPhiOnly(!phiOnly); setPage(1); }}
                  data-testid="phi-filter-btn"
                >
                  <Lock className="w-4 h-4 mr-1" />
                  PHI Only
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportLogs} data-testid="export-btn">
                  <Download className="w-4 h-4 mr-1" />
                  Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Audit Log Table */}
          <Card className="bg-white">
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Timestamp</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">User</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Action</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Resource</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">PHI</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="border-b hover:bg-slate-50" data-testid={`audit-row-${log.id}`}>
                      <td className="p-3 text-sm text-slate-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{log.user_name}</p>
                          <p className="text-xs text-slate-400">{log.user_role}</p>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge className={getActionColor(log.action)}>{log.action}</Badge>
                      </td>
                      <td className="p-3">
                        <div>
                          <p className="text-sm text-slate-800">{log.resource_type}</p>
                          {log.resource_name && (
                            <p className="text-xs text-slate-400 truncate max-w-[200px]">{log.resource_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        {log.success ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </td>
                      <td className="p-3">
                        {log.phi_accessed && <Lock className="w-4 h-4 text-orange-500" />}
                      </td>
                      <td className="p-3">
                        <Button variant="ghost" size="sm" onClick={() => handleViewLogDetail(log.id)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-slate-500">
                  Showing {((auditPagination.page - 1) * auditPagination.page_size) + 1} - {Math.min(auditPagination.page * auditPagination.page_size, auditPagination.total_items)} of {auditPagination.total_items}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-slate-600">Page {page} of {auditPagination.total_pages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(auditPagination.total_pages, p + 1))}
                    disabled={page >= auditPagination.total_pages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {integrations.map((integration) => (
              <Card key={integration.id} className="bg-white" data-testid={`integration-${integration.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        integration.status === 'healthy' ? 'bg-green-100' : 
                        integration.status === 'degraded' ? 'bg-yellow-100' : 'bg-red-100'
                      }`}>
                        {getIntegrationIcon(integration.type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-800">{integration.name}</h3>
                          {getStatusBadge(integration.status)}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{integration.endpoint}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span>Response: {integration.response_time_ms}ms</span>
                          <span>Uptime: {integration.uptime_percent}%</span>
                          <span>Errors: {integration.error_count_24h}</span>
                        </div>
                        {integration.last_error && (
                          <p className="text-xs text-red-500 mt-1">Last error: {integration.last_error}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleTestIntegration(integration.id)} data-testid={`test-${integration.id}`}>
                        Test
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleViewIntegrationDetail(integration.id)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* API Performance Tab */}
        <TabsContent value="api" className="space-y-4">
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg">API Endpoint Performance</CardTitle>
              <CardDescription>Response times and error rates for the last 24 hours</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Endpoint</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Method</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Requests</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Avg (ms)</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">P95 (ms)</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">P99 (ms)</th>
                    <th className="text-left p-3 text-sm font-medium text-slate-600">Error Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {apiMetrics.map((metric, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50" data-testid={`api-row-${idx}`}>
                      <td className="p-3 text-sm font-mono text-slate-800">{metric.endpoint}</td>
                      <td className="p-3">
                        <Badge variant="outline">{metric.method}</Badge>
                      </td>
                      <td className="p-3 text-sm text-slate-600">{metric.requests_24h.toLocaleString()}</td>
                      <td className="p-3 text-sm text-slate-600">{metric.avg_response_time_ms}</td>
                      <td className="p-3 text-sm text-slate-600">{metric.p95_response_time_ms}</td>
                      <td className="p-3 text-sm text-slate-600">{metric.p99_response_time_ms}</td>
                      <td className="p-3">
                        <Badge className={metric.error_rate_percent > 2 ? 'bg-red-100 text-red-700' : metric.error_rate_percent > 1 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}>
                          {metric.error_rate_percent}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Log Detail Dialog */}
      <Dialog open={logDetailOpen} onOpenChange={setLogDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Audit Log Details</DialogTitle>
            <DialogDescription>Full details for this audit event</DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Timestamp</p>
                  <p className="font-medium">{new Date(selectedLog.log.timestamp).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Action</p>
                  <Badge className={getActionColor(selectedLog.log.action)}>{selectedLog.log.action}</Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">User</p>
                  <p className="font-medium">{selectedLog.log.user_name}</p>
                  <p className="text-xs text-slate-400">{selectedLog.log.user_email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Role</p>
                  <p className="font-medium">{selectedLog.log.user_role}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Resource</p>
                  <p className="font-medium">{selectedLog.log.resource_type}</p>
                  {selectedLog.log.resource_name && (
                    <p className="text-xs text-slate-400">{selectedLog.log.resource_name}</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-500">IP Address</p>
                  <p className="font-medium font-mono">{selectedLog.log.ip_address}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="font-medium">{selectedLog.log.location}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">PHI Accessed</p>
                  <p className="font-medium flex items-center gap-1">
                    {selectedLog.log.phi_accessed ? (
                      <><Lock className="w-4 h-4 text-orange-500" /> Yes</>
                    ) : (
                      <><Unlock className="w-4 h-4 text-gray-400" /> No</>
                    )}
                  </p>
                </div>
              </div>
              
              {selectedLog.log.failure_reason && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600 font-medium">Failure Reason</p>
                  <p className="text-sm text-red-700">{selectedLog.log.failure_reason}</p>
                </div>
              )}

              {selectedLog.related_logs?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Related Activity (±1 hour)</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedLog.related_logs.map((rel) => (
                      <div key={rel.id} className="text-xs p-2 bg-slate-50 rounded flex items-center justify-between">
                        <span>{rel.action} - {rel.resource_type}</span>
                        <span className="text-slate-400">{new Date(rel.timestamp).toLocaleTimeString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Integration Detail Dialog */}
      <Dialog open={integrationDetailOpen} onOpenChange={setIntegrationDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Integration Details</DialogTitle>
            <DialogDescription>Status and performance history</DialogDescription>
          </DialogHeader>
          {selectedIntegration && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  selectedIntegration.integration.status === 'healthy' ? 'bg-green-100' : 
                  selectedIntegration.integration.status === 'degraded' ? 'bg-yellow-100' : 'bg-red-100'
                }`}>
                  {getIntegrationIcon(selectedIntegration.integration.type)}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{selectedIntegration.integration.name}</h3>
                  <p className="text-sm text-slate-500">{selectedIntegration.integration.endpoint}</p>
                </div>
                {getStatusBadge(selectedIntegration.integration.status)}
              </div>

              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-slate-500">Avg Response</p>
                    <p className="text-lg font-bold">{selectedIntegration.metrics?.avg_response_time_24h || 0}ms</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-slate-500">Min Response</p>
                    <p className="text-lg font-bold">{selectedIntegration.metrics?.min_response_time_24h || 0}ms</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-slate-500">Max Response</p>
                    <p className="text-lg font-bold">{selectedIntegration.metrics?.max_response_time_24h || 0}ms</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-slate-500">Success Rate</p>
                    <p className="text-lg font-bold">{Math.round((selectedIntegration.metrics?.healthy_checks / selectedIntegration.metrics?.total_checks) * 100) || 0}%</p>
                  </CardContent>
                </Card>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">24-Hour Status History</p>
                <div className="flex gap-1">
                  {selectedIntegration.history_24h?.slice(0, 24).map((h, idx) => (
                    <div
                      key={idx}
                      className={`w-3 h-8 rounded ${
                        h.status === 'healthy' ? 'bg-green-400' :
                        h.status === 'degraded' ? 'bg-yellow-400' : 'bg-red-400'
                      }`}
                      title={`${new Date(h.timestamp).toLocaleTimeString()}: ${h.status}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
